import React, { useLayoutEffect, useRef, useState } from 'react';
import Styles from './SlaPills.module.css';
import TicketStyle from '../TicketDetailInfo/TicketDetailInfo.module.css';

import {
  Flex,
  Text,
  ProviderConsumer as FluentUIThemeConsumer,
  Skeleton,
  Tooltip
} from '@fluentui/react-northstar';
import BreachingInSVG from '../../../../svg/slas.svg';
import {
  SLAActionType,
  SlaStateStrings,
  TicketSlaState,
  TICKET_DETAIL
} from '../../utils/constants';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { COUNT_STYLE, PILL_GAP } from '../table/PillList';
import { pillsThatFit } from '../table/tagPillsFit';

const Title = ({ text }) => (
  <Text size="small" style={{ color: '#8f90a6', marginLeft: '4px' }}>
    {text}:
  </Text>
);

type Props = {
  ticketSlas: TicketSla[];
  isLoading?: boolean;
  svgGap: boolean;
  viewType?: 'Board' | 'Edit' | 'List';
  textSize?: 'largest' | 'larger' | 'large' | 'medium' | 'small' | 'smaller' | 'smallest';
  /**
   * A list's SLA cell: as many pills as the column has room for, then "+N" - as the tags and other multi-select cells
   * do, widening the column brings folded pills back - with their "+N" style and gap. Without it, 'List' shows one.
   */
  fitToCell?: boolean;
};

const SkeletonItemStyle = {
  width: '24px',
  height: '14px',
  style: {
    borderRadius: '7px'
  }
};

// The tooltip writes these states in lighter shades of their pill's colour: the card is Northstar's grey[500] in both
// themes, and the pill colours read at 2.3-3.4:1 on it.
const TOOLTIP_STATE_COLOURS: Record<string, string> = {
  [TicketSlaState.Active]: '#4fd18b',
  [TicketSlaState.Breached]: '#ff8f8f',
  [TicketSlaState.ApproachBreached]: '#f4a259'
};
const tooltipStateStyle = (state: string): React.CSSProperties | undefined =>
  TOOLTIP_STATE_COLOURS[state] ? { color: TOOLTIP_STATE_COLOURS[state] } : undefined;

/** Each SLA with a React key: its name, numbered when two SLAs share one, so keys stay unique. */
const withKeys = (slas: SlaCalculate[]) => {
  const used = new Set<string>();
  return slas.map(sla => {
    let key = sla.name;
    let n = 1;
    while (used.has(key)) key = `${sla.name}#${n++}`;
    used.add(key);
    return { sla, key };
  });
};

const PillsColors = {
  [TicketSlaState.Active]: {
    backgroundColor: '#e3fff1',
    color: '#05a660'
  },
  [TicketSlaState.Breached]: {
    backgroundColor: '#ffe6e6',
    color: '#e53535'
  },
  [TicketSlaState.ApproachBreached]: {
    backgroundColor: '#fff8e6',
    color: '#e57a00'
  },
  // Tailwind gray-200 / gray-600, the same as CSS_CLASS.STATUS_PILL_CLOSED, so a met SLA and a
  // closed ticket read as one "done" grey. The lighter #f2f2f5 vanished on the list's #f5f5f5 rows.
  [TicketSlaState.Satisfied]: {
    backgroundColor: '#e5e7eb',
    color: '#4b5563'
  }
};

export const SlaPills = ({
  isLoading = false,
  ticketSlas = [],
  svgGap = true,
  viewType = 'Edit',
  textSize = 'small',
  fitToCell = false
}: Props) => {

  const boxRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  // Starts with every pill, as PillList does: a double-click on the column's divider sizes it from each cell's first
  // render, so starting at one fitted the column to one pill and its "+N".
  const [fitted, setFitted] = useState(Math.max(1, ticketSlas.length));

  const durationCalculator = (
    currentDateAndTime: DateTime,
    assignDateAndTime: DateTime,
    slaCriteria: SlaCriterias
  ) => {
    const _assignDateAndTimePlusDuration = assignDateAndTime.plus({
      [slaCriteria.TimeUnitPart.toLowerCase()]: slaCriteria.TimeDigitPart
    });
    const { days, hours, minutes } = _assignDateAndTimePlusDuration
      .diff(currentDateAndTime, ['days', 'hours', 'minutes'])
      .toObject();
      if (days > 0) {
          return `${days}d`;
      } else if (hours > 0) {
          return `${hours}h`;
      } else if (minutes > 0) {
          return `${Math.round(minutes)}m`;
      } else if (minutes < 0) {
          return '0m';
      }
  };

  const calculateSlaBreaching = (_ticketSlas: TicketSla[]) => {
    const currentDateAndTime = DateTime.now();
    const _slaData: SlaCalculate[] = [];
    _ticketSlas?.forEach(
      ({ ServiceLevelAgreement, SlaState, CreatedDate, ModifiedDate }) => {
        let _sla: SlaCalculate = {
          name: ServiceLevelAgreement.Name,
          style: {
            color: '',
            backgroundColor: ''
          }
        };

        const _approachBreach = ServiceLevelAgreement.SlaCriterias.find(
          criteria => criteria.SlaActionTypeId === SLAActionType.ApproachBreach
        );
        const _breach = ServiceLevelAgreement.SlaCriterias.find(
          criteria => criteria.SlaActionTypeId === SLAActionType.Breached
        );
        const _timeStamp = DateTime.fromJSDate(new Date(ModifiedDate)).toFormat(
          'LL/dd/yy hh:mm a'
        );
        const slaAssignTime = DateTime.fromJSDate(new Date(CreatedDate));

        if (
          SlaState.Name === TicketSlaState.Breached ||
          SlaState.Name === TicketSlaState.Satisfied
        ) {
          const _modifiedDate = DateTime.fromJSDate(new Date(ModifiedDate));
          const diff = durationCalculator(
            _modifiedDate,
            slaAssignTime,
            _breach
          );
          _sla['duration'] = diff;
          _sla['style'] = PillsColors[SlaState.Name];
          _sla['timeStamp'] = _timeStamp;
          _sla['state'] = SlaState.Name;
          _sla['stateString'] = SlaStateStrings[SlaState.Name];
        } else {
          const diff = durationCalculator(
            currentDateAndTime,
            slaAssignTime,
            _breach
          );
          _sla['duration'] = diff;
          _sla['style'] = PillsColors[SlaState.Name];
          _sla['timeStamp'] = _timeStamp;
          _sla['state'] = SlaState.Name;
          _sla['stateString'] = SlaStateStrings[SlaState.Name];
        }

        _slaData.push(_sla);
      }
    );
    return _slaData;
  };
  const {t} = useTranslation();
  const slas = calculateSlaBreaching(ticketSlas);
  const pillText = (sla: SlaCalculate) =>
    sla.state === TicketSlaState.Breached || sla.state === TicketSlaState.Satisfied ? sla.stateString : sla.duration;
  // By content: the list is built afresh on every render.
  const slasKey = slas.map(sla => `${sla.name}|${pillText(sla)}`).join(',');

  // Measured once, out of sight, as PillList does; fitted again whenever the cell's width changes. Never fewer than one
  // pill - a cell does not show "+N" alone.
  useLayoutEffect(() => {
    const box = boxRef.current;
    const measure = measureRef.current;
    if (!fitToCell || !box || !measure || slas.length === 0) return undefined;
    const items = Array.from(measure.children) as HTMLElement[];
    const widths = items.slice(0, slas.length).map(item => item.getBoundingClientRect().width);
    const plusWidth = items[slas.length].getBoundingClientRect().width;
    const fit = () => setFitted(Math.max(1, pillsThatFit(widths, box.clientWidth, PILL_GAP, plusWidth)));
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [fitToCell, slasKey]);

  let pillsToShow = viewType === 'List' ? 1 : 3;
  if (fitToCell) pillsToShow = fitted;
  const keyedSlas = withKeys(slas);
  const renderSlasPills = () => (
    <>
      {slas.length > 0 ? (
        <>
          {keyedSlas.slice(0, pillsToShow).map(({ sla: { name, style, duration, state, timeStamp, stateString }, key }) => (

            <Tooltip
              key={key}
              align={"center"}
              position={"below"}
              trigger={<div className={`font-bold relative inline-block ${fitToCell ? 'cursor-default' : 'cursor-pointer'} ${Styles['SlaPills']}`}>
                        <span className={Styles.Pills} style={{ ...style }}>
                          {pillText({ state, stateString, duration } as SlaCalculate)}
                        </span>
                        
                      </div>}
              content={<>
                <div className={Styles.tooltipcontent}>
                          <div className="px-2">
                            <p className={`${Styles.SlaName} ${Styles.MarginBottom} font-medium`}>{name}</p>
                            <p className={`${Styles.SlaDesc} ${Styles.MarginBottom} font-medium`}>
                              <span style={tooltipStateStyle(state)}>{stateString}</span>{' '}
                              {state !== TicketSlaState.Breached && state !== TicketSlaState.Satisfied && (
                                <span className="mx-0.5" style={tooltipStateStyle(state) ?? { color: style.color }}>
                                  {duration}
                                </span>
                              )}
                            </p>
                            <p className={Styles.SlaTime}>{timeStamp}</p>
                          </div>
                        </div>
              </>}
            />



            
          ))}
        </>
      ) : (
        <Text size={textSize} weight="regular">{t('ticket-details.ticket-details-card.ticket-details-info.none')}</Text>
      )}
    </>
  );

  return (
    <FluentUIThemeConsumer render={_globalTheme => (
        <Flex vAlign="center" gap="gap.small" styles={fitToCell ? { width: '100%', minWidth: 0 } : undefined}>
          {viewType !== 'List' && (<Flex vAlign="center" className={TicketStyle.fieldDetailsCol0} {...(svgGap ? { gap: 'gap.smaller' } : {})}>
            <BreachingInSVG width={viewType === 'Edit' ? 12 : 14} height={viewType === 'Edit' ? 12 : 14} {...(viewType === 'Edit' && { style: { marginRight: '0.5rem' } })}/> 
            <Title text={TICKET_DETAIL.SLAS} />
          </Flex>)}
          <div
            ref={boxRef}
            className={fitToCell ? 'flex items-center w-full overflow-hidden relative py-0.5' : 'flex items-center gap-1'}
            style={fitToCell ? { gap: PILL_GAP, minWidth: 0 } : undefined}
          >
            {isLoading ? (
              <Skeleton animation="wave">
                <Flex gap="gap.small">
                  <Skeleton.Shape {...SkeletonItemStyle} />
                  <Skeleton.Shape {...SkeletonItemStyle} />
                  <Skeleton.Shape {...SkeletonItemStyle} />
                </Flex>
              </Skeleton>
            ) : (
              renderSlasPills()
            )}
            {slas.length > pillsToShow && (
              <div className={fitToCell ? 'relative inline-block cursor-default' : `${Styles.tooltip} relative inline-block cursor-pointer`}
                style={fitToCell ? { alignSelf: 'center', flexShrink: 0 } : undefined}>
                <Tooltip
                  align={"center"}
                  position={"below"}
                  trigger={<div>
                        {/* Not lifted as the tags' count is: next to the smaller SLA pill that put it 2.7px above the pill's
                            middle; centred it sits 1.2px above, where it always was. */}
                        <span style={fitToCell ? { ...COUNT_STYLE, top: 0 } : undefined}>
                          +{slas.slice(pillsToShow).length}
                        </span>
                      </div>}
                  content={
                      <div>
                        <div className="flex flex-col h-full overflow-auto">
                          {slas.slice(pillsToShow).map(({ name, state, style, duration, timeStamp, stateString}, index) => (
                                <div key={index} className={`${Styles.SlaItem} px-2`}>
                                  <p className={`${Styles.SlaName} ${Styles.MarginBottom} font-medium`}>{name}</p>
                                  <p className={`${Styles.SlaDesc} ${Styles.MarginBottom} font-medium`}>
                                    <span style={tooltipStateStyle(state)}>{stateString}</span>{' '}
                                    {state !== TicketSlaState.Breached && state !== TicketSlaState.Satisfied && (
                                      <span className="mx-0.5" style={tooltipStateStyle(state) ?? { color: style.color }}>
                                        {duration}
                                      </span>
                                    )}
                                  </p>
                                  <p className={Styles.SlaTime}>{timeStamp}</p>
                                </div>
                              )
                            )}
                        </div>
                      </div>
                  }
                />
              </div>
            )}
            {fitToCell && slas.length > 0 && (
              <div ref={measureRef} aria-hidden className="flex absolute" style={{ visibility: 'hidden', left: 0, top: 0, pointerEvents: 'none' }}>
                {keyedSlas.map(({ sla, key }) => (
                  <div key={key} className={`font-bold relative inline-block ${Styles['SlaPills']}`}>
                    <span className={Styles.Pills} style={{ ...sla.style }}>{pillText(sla)}</span>
                  </div>
                ))}
                <span style={COUNT_STYLE}>+{slas.length}</span>
              </div>
            )}
          </div>
        </Flex>
      )}
    />
  );
};
